/**
 * pendingSends — localStorage persistence layer for optimistic send state.
 *
 * Lets the client resync after reload: when handleSendMessage fires, we stash
 * a lightweight record keyed by cliMsgId. After the send reconciles (success,
 * error, or reload + server lookup), the record is removed. On mount we read
 * back any leftover records and reconcile them against the server outbox.
 *
 * Scope is per-browser (shared across tabs). TTL 24h to avoid unbounded growth
 * from crashes that never clean up.
 */

const STORAGE_KEY = "akabiz.pendingSends.v1";
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const readAll = () => {
  if (typeof localStorage === "undefined") return {};
  const map = safeParse(localStorage.getItem(STORAGE_KEY));
  const now = Date.now();
  // Drop entries older than TTL as we read — lazy cleanup.
  let mutated = false;
  for (const [id, entry] of Object.entries(map)) {
    if (!entry?.ts || now - entry.ts > TTL_MS) {
      delete map[id];
      mutated = true;
    }
  }
  if (mutated) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
      /* storage full — best effort */
    }
  }
  return map;
};

const writeAll = (map) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* storage full — best effort */
  }
};

/**
 * Add or overwrite a pending record.
 * @param {string} cliMsgId
 * @param {object} entry - { shopId, contactUid, type, content, ... }
 */
export const addPending = (cliMsgId, entry) => {
  if (!cliMsgId) return;
  const map = readAll();
  map[cliMsgId] = { ...entry, ts: Date.now() };
  writeAll(map);
};

export const removePending = (cliMsgId) => {
  if (!cliMsgId) return;
  const map = readAll();
  if (map[cliMsgId]) {
    delete map[cliMsgId];
    writeAll(map);
  }
};

/** Group pending records by shopId for efficient bulk status lookup. */
export const getPendingByShop = () => {
  const map = readAll();
  const byShop = {};
  for (const [cliMsgId, entry] of Object.entries(map)) {
    if (!entry?.shopId) continue;
    if (!byShop[entry.shopId]) byShop[entry.shopId] = [];
    byShop[entry.shopId].push({ cliMsgId, ...entry });
  }
  return byShop;
};

/** Entire map — used in tests. */
export const getAllPending = () => readAll();

/** Forget every entry — e.g. on logout. */
export const clearAllPending = () => writeAll({});
